import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { Button, Table, Form, Card, Alert, InputGroup } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useStores } from "../../context/mobx-context";

function ShortenedLinks() {
  const { shortenedLinksStore } = useStores();
  const [newUrl, setNewUrl] = useState("");
  const [showAlert, setShowAlert] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const history = useHistory();

  useEffect(() => {
    if (shortenedLinksStore.urls.length === 0) {
      shortenedLinksStore.fetchUrls();
    }
    // Sync search input with store
    setSearchInput(shortenedLinksStore.searchQuery);
  }, [shortenedLinksStore]);

  const handleSearch = (e) => {
    e.preventDefault();
    shortenedLinksStore.setSearchQuery(searchInput);
  };

  const clearSearch = () => {
    setSearchInput("");
    shortenedLinksStore.setSearchQuery("");
  };

  const handleShortenUrl = async () => {
    if (!newUrl.trim()) {
      setShowAlert({ type: "danger", message: "Please enter a URL to shorten" });
      return;
    }

    try {
      const result = await shortenedLinksStore.createShortUrl(newUrl);
      setShowAlert({ type: "success", message: `Short URL created: ${result.shortUrl}` });
      setNewUrl("");
      // Refresh the list to include the newly created URL
      await shortenedLinksStore.fetchUrls();
    } catch (error) {
      setShowAlert({ type: "danger", message: error.message || "Failed to create short URL" });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setShowAlert({ type: "success", message: "Copied to clipboard!" });
      })
      .catch(() => {
        setShowAlert({ type: "danger", message: "Failed to copy to clipboard" });
      });
  };

  const handleDeleteUrl = async (shortCode) => {
    if (!window.confirm("Are you sure you want to delete this short URL? This action cannot be undone.")) {
      return;
    }

    try {
      await shortenedLinksStore.deleteShortUrl(shortCode);
      setShowAlert({ type: "success", message: "Short URL deleted successfully" });
      // Refresh the list to remove the deleted URL
      await shortenedLinksStore.fetchUrls();
    } catch (error) {
      setShowAlert({ type: "danger", message: error.message || "Failed to delete short URL" });
    }
  };

  const extractPathFromUrl = (originalUrl) => {
    try {
      // Extract path from URLs like "https://app.whering.co.uk/app-link/profile/123?utm_source=email"
      const url = new URL(originalUrl);
      const pathname = url.pathname;

      // Remove "/app-link/" prefix to get the actual deeplink path
      if (pathname.startsWith("/app-link/")) {
        return pathname.substring("/app-link/".length);
      }

      return pathname;
    } catch (error) {
      // If URL parsing fails, try to extract path manually
      const match = originalUrl.match(/\/app-link\/([^?]+)/);
      return match ? match[1] : null;
    }
  };

  const handleViewAnalytics = (originalUrl) => {
    const path = extractPathFromUrl(originalUrl);
    if (path) {
      // Navigate to the dedicated analytics page for this specific deeplink path
      history.push(`/deeplinks/analytics/${encodeURIComponent(path)}`);
    } else {
      setShowAlert({ type: "danger", message: "Unable to extract path from URL for analytics" });
    }
  };

  return (
    <div className="container-fluid px-4 py-3">
      <div className="mb-4 p-3 bg-light rounded">
        <h2 className="mb-1">🔗 URL Shortening Service</h2>
        <p className="text-muted mb-0">Create and manage shortened URLs for Whering app-links</p>
      </div>

      {showAlert && (
        <Alert variant={showAlert.type} onClose={() => setShowAlert(null)} dismissible className="mb-4 shadow-sm">
          <div className="d-flex align-items-center">
            <span className="me-2">{showAlert.type === "success" ? "✅" : "❌"}</span>
            {showAlert.message}
          </div>
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary text-white">
          <h5 className="mb-0">✨ Create New Short URL</h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleShortenUrl();
            }}
          >
            <InputGroup size="lg">
              <Form.Control
                type="url"
                placeholder="Enter Whering app-link URL (e.g., https://app.whering.co.uk/app-link/moodboards?utm_source=twitter&utm_medium=social)"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                disabled={shortenedLinksStore.isCreating}
                className="fs-6"
              />
              <Button
                variant="dark"
                onClick={handleShortenUrl}
                disabled={shortenedLinksStore.isCreating}
                className="px-4"
              >
                {shortenedLinksStore.isCreating ? "⏳ Creating..." : "🔗 Shorten URL"}
              </Button>
            </InputGroup>
            <Form.Text className="text-muted mt-2">
              Only Whering app-link URLs can be shortened. You can include UTM parameters (utm_source, utm_medium,
              utm_campaign etc.) for tracking campaigns and referral sources.
            </Form.Text>
          </Form>
        </Card.Body>
      </Card>

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary text-white">
          <h5 className="mb-0">🔍 Search Shortened URLs</h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Form onSubmit={handleSearch}>
            <InputGroup size="lg">
              <Form.Control
                type="text"
                placeholder="Search by original URL..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="fs-6"
              />
              <Button variant="outline-dark" type="submit" className="px-4">
                🔍 Search
              </Button>
              {shortenedLinksStore.searchQuery && (
                <Button variant="outline-secondary" onClick={clearSearch} className="px-3">
                  ✖️ Clear
                </Button>
              )}
            </InputGroup>
            <Form.Text className="text-muted mt-2">
              Search through your shortened URLs by their original URL content.
            </Form.Text>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header className="bg-dark text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">📋 Your Shortened URLs</h5>
            <small className="text-light">{shortenedLinksStore.totalUrls} total URLs</small>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {shortenedLinksStore.isFetching ? (
            <div className="text-center p-5">
              <div className="spinner-border text-secondary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="text-muted mb-2">Loading Shortened URLs</h5>
              <p className="text-muted mb-0">Please wait while we fetch your shortened links...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th className="text-start px-4 py-3">#</th>
                    <th className="text-start px-4 py-3">Short Code</th>
                    <th className="text-start px-4 py-3">Short URL</th>
                    <th className="text-start px-4 py-3">Original URL</th>
                    <th className="text-start px-4 py-3">Created</th>
                    <th className="text-start px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shortenedLinksStore.urls.map((url, index) => (
                    <tr key={url.shortCode} className={index % 2 === 0 ? "table-light" : ""}>
                      <td className="text-start px-4 py-3">
                        <span className="badge bg-secondary">
                          {(shortenedLinksStore.currentPage - 1) * shortenedLinksStore.pageSize + index + 1}
                        </span>
                      </td>
                      <td className="text-start px-4 py-3">
                        <code className="bg-light border px-2 py-1 rounded fw-bold text-dark">{url.shortCode}</code>
                      </td>
                      <td className="text-start px-4 py-3" style={{ maxWidth: "200px" }}>
                        <a
                          href={url.shortUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <code className="text-primary text-wrap d-block fw-semibold">{url.shortUrl}</code>
                        </a>
                      </td>
                      <td className="text-start px-4 py-3" style={{ maxWidth: "250px" }}>
                        <small className="text-wrap d-block">{url.originalUrl}</small>
                      </td>
                      <td className="text-start px-4 py-3">
                        <small className="text-muted">{formatDate(url.createdAt)}</small>
                      </td>
                      <td className="text-start px-4 py-3">
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => copyToClipboard(url.shortUrl)}
                            className="mb-1"
                          >
                            📋 Copy
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewAnalytics(url.originalUrl)}
                            className="mb-1"
                          >
                            📊 Analytics
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteUrl(url.shortCode)}
                            disabled={shortenedLinksStore.deletingUrls.has(url.shortCode)}
                            className="mb-1"
                          >
                            {shortenedLinksStore.deletingUrls.has(url.shortCode) ? "⏳ Deleting..." : "🗑️ Delete"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {shortenedLinksStore.hasFetched && shortenedLinksStore.urls.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center p-5">
                        <div className="mb-3">
                          <span className="display-1">🔍</span>
                        </div>
                        <h4 className="text-muted mb-3">No Shortened URLs Found</h4>
                        <p className="text-muted mb-0">
                          {shortenedLinksStore.searchQuery
                            ? `No URLs match your search for "${shortenedLinksStore.searchQuery}"`
                            : "Create your first shortened URL using the form above"}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {shortenedLinksStore.totalUrls > shortenedLinksStore.pageSize && (
        <Card className="mt-4 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">Pagination</h6>
                <small className="text-muted">
                  Showing {Math.min(shortenedLinksStore.urls.length, shortenedLinksStore.pageSize)} of{" "}
                  {shortenedLinksStore.totalUrls} shortened URLs
                </small>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="outline-secondary"
                  disabled={shortenedLinksStore.currentPage === 1 || shortenedLinksStore.isFetching}
                  onClick={() => shortenedLinksStore.setCurrentPage(shortenedLinksStore.currentPage - 1)}
                >
                  ← Previous
                </Button>
                <span className="badge bg-secondary px-3 py-2 fs-6">
                  Page {shortenedLinksStore.currentPage} of {shortenedLinksStore.totalPages}
                </span>
                <Button
                  variant="outline-secondary"
                  disabled={
                    shortenedLinksStore.currentPage >= shortenedLinksStore.totalPages || shortenedLinksStore.isFetching
                  }
                  onClick={() => shortenedLinksStore.setCurrentPage(shortenedLinksStore.currentPage + 1)}
                >
                  Next →
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default observer(ShortenedLinks);
